import Seo from "../components/Seo";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, BookOpen, Gift } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { COMUNE_COORDS, GENERI } from "../lib/constants";
import Stars from "../components/Stars";
import ShareMenu from "../components/ShareMenu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";

function countIcon(count) {
  return L.divIcon({
    className: "",
    html: `<div style="background:#8C3A27;color:#fff;width:38px;height:38px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,.35);border:2px solid #fff"><span style="transform:rotate(45deg);font-weight:700;font-size:13px;font-family:'Plus Jakarta Sans',sans-serif">${count}</span></div>`,
    iconSize: [38, 38],
    iconAnchor: [19, 38],
    popupAnchor: [0, -34],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 12); return; }
    map.fitBounds(points, { padding: [50, 50], maxZoom: 12 });
  }, [map, points]);
  return null;
}

export default function MapPage() {
  const [books, setBooks] = useState([]);
  const [genre, setGenre] = useState("all");
  const [onlyGift, setOnlyGift] = useState(false);

  useEffect(() => { api.get("/books").then(({ data }) => setBooks(data)); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const b of books) {
      if (b.is_demo) continue;
      if (!COMUNE_COORDS[b.comune]) continue;
      if (genre !== "all" && b.genre !== genre) continue;
      if (onlyGift && !b.is_gift) continue;
      (g[b.comune] = g[b.comune] || []).push(b);
    }
    return g;
  }, [books, genre, onlyGift]);

  const total = Object.values(grouped).reduce((a, l) => a + l.length, 0);
  const comuniCount = Object.keys(grouped).length;
  const points = useMemo(() => Object.keys(grouped).map((c) => COMUNE_COORDS[c]), [grouped]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Seo title="Mappa dei libri da scambiare | Libri in Circolo" description="Esplora sulla mappa i libri da scambiare e regalare vicino a te. Trova i libri disponibili nel tuo comune e nelle zone limitrofe." path="/mappa" />
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center"><MapPin className="w-6 h-6" /></div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">Mappa dei comuni</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-4" data-testid="map-stats">
        <span className="font-semibold text-foreground">{comuniCount}</span> {comuniCount === 1 ? "comune attivo" : "comuni attivi"} · <span className="font-semibold text-foreground">{total}</span> {total === 1 ? "libro" : "libri"} sul territorio. Tocca un segnaposto per vedere i libri disponibili in quel comune.
      </p>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
        <div className="w-full sm:w-52">
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger data-testid="map-filter-genre" className="w-full"><SelectValue placeholder="Genere" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i generi</SelectItem>
              {GENERI.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="map-filter-gift" variant={onlyGift ? "default" : "outline"}
          onClick={() => setOnlyGift((v) => !v)}
          className={`flex-1 sm:flex-none ${onlyGift ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}>
          <Gift className="w-4 h-4 mr-2" />Solo in regalo
        </Button>
        {(genre !== "all" || onlyGift) && (
          <Button variant="ghost" size="sm" onClick={() => { setGenre("all"); setOnlyGift(false); }} data-testid="map-filter-reset">Azzera</Button>
        )}
        <div className="w-full sm:w-auto sm:ml-auto">
          <ShareMenu
            text={`Scopri i libri disponibili vicino a te su Libri in Circolo: ${typeof window !== "undefined" ? window.location.href : ""}`}
            align="end" testid="map-share" />
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-border" style={{ height: "min(70vh, 620px)" }} data-testid="comuni-map">
        <MapContainer center={[42.17, 12.56]} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={points} />
          {Object.entries(grouped).map(([comune, list]) => (
            <Marker key={comune} position={COMUNE_COORDS[comune]} icon={countIcon(list.length)}>
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <p style={{ fontWeight: 700, marginBottom: 6 }}>{comune} · {list.length} libri</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
                    {list.slice(0, 12).map((b) => (
                      <a key={b.id} href={`/libro/${b.id}`} style={{ display: "flex", gap: 8, alignItems: "center", textDecoration: "none", color: "inherit" }}>
                        <span style={{ width: 30, height: 42, background: "#eee", borderRadius: 4, overflow: "hidden", flexShrink: 0, display: "inline-block" }}>
                          {fileUrl(b) && <img src={fileUrl(b)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                        </span>
                        <span style={{ fontSize: 12, lineHeight: 1.3 }}>
                          <strong>{b.title}</strong><br />{b.author}
                          {b.owner?.rating?.count > 0 && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                              <Stars value={b.owner.rating.average} size={11} />
                              <span style={{ color: "#8a7f76" }}>{b.owner.rating.average}</span>
                            </span>
                          )}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {total === 0 && (
        <div className="text-center py-10 text-muted-foreground flex flex-col items-center gap-2">
          <BookOpen className="w-8 h-8" />Nessun libro reale ancora sulla mappa. <Link to="/pubblica" className="text-primary underline">Pubblica il primo!</Link>
        </div>
      )}
    </div>
  );
}
